import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

type ArticleTypeBarChartData = {
  rows: Array<Record<string, string | number>>;
  series: string[];
};

interface Props {
  data: ArticleTypeBarChartData;
  showAllRevisions?: boolean;
  axes?: {
    x?: string;
    y?: string;
  };
}

const BAR_COLORS = [
  '#2563EB',
  '#0F766E',
  '#D97706',
  '#9333EA',
  '#DC2626',
  '#0891B2',
  '#4F46E5',
  '#65A30D'
];

export const ArticleTypeBarChart = ({
  data,
  showAllRevisions = false,
  axes
}: Props) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const rows = showAllRevisions
    ? data.rows
    : data.rows.filter(row => {
        const revision = String(row.revision ?? '');
        const revisionNumber = Number(revision.replace(/^R/i, ''));

        return Number.isNaN(revisionNumber) || revisionNumber <= 3;
      });
  const shouldRotateXAxisLabels = isMobile || rows.length > 4;
  const xAxisHeight = shouldRotateXAxisLabels ? 56 : 30;
  const chartHeight = isMobile ? (axes?.x ? 292 : 316) : axes?.x ? 316 : 340;

  return (
    <Box>
      <Flex align="stretch">
        {axes?.y ? (
          <Flex align="center" justify="center" pr={2} minW="28px">
            <Text
              textStyle="textContext"
              textAlign="center"
              sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {axes.y}
            </Text>
          </Flex>
        ) : null}
        <Box flex="1">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={rows}
              margin={{
                top: 24,
                right: isMobile ? 8 : 16,
                left: isMobile ? 0 : 16,
                bottom: shouldRotateXAxisLabels ? 20 : 12
              }}
            >
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#D9E2EC"
                vertical={false}
              />
              <XAxis
                dataKey="revision"
                axisLine={false}
                tickLine={false}
                height={xAxisHeight}
                interval={0}
                angle={shouldRotateXAxisLabels ? -20 : 0}
                textAnchor={shouldRotateXAxisLabels ? 'end' : 'middle'}
                tickMargin={shouldRotateXAxisLabels ? 10 : 6}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={isMobile ? 32 : 40}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip
                cursor={{ fill: '#E2E8F0' }}
                allowEscapeViewBox={{ x: true, y: true }}
                wrapperStyle={{ outline: 'none', zIndex: 20 }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) {
                    return null;
                  }

                  return (
                    <Box
                      w="460px"
                      maxW="calc(100vw - 32px)"
                      p={3}
                      border="1px solid #CBD5E1"
                      borderRadius="12px"
                      bg="white"
                      boxShadow="0 12px 28px rgba(15, 23, 42, 0.14)"
                    >
                      <Text mb={2} fontSize="sm" fontWeight="600">
                        {label}
                      </Text>
                      <Box
                        display="grid"
                        gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                        columnGap={3}
                        rowGap={1}
                      >
                        {payload.map(item => (
                          <Text
                            key={String(item.dataKey)}
                            fontSize="sm"
                            color={item.color}
                            noOfLines={1}
                          >
                            {item.name}: {item.value}
                          </Text>
                        ))}
                      </Box>
                    </Box>
                  );
                }}
              />
              {data.series.map((seriesKey, index) => (
                <Bar
                  key={seriesKey}
                  dataKey={seriesKey}
                  fill={BAR_COLORS[index % BAR_COLORS.length]}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={42}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
      <Flex
        mt={3}
        justify="center"
        wrap="wrap"
        columnGap={3}
        rowGap={1}
      >
        {data.series.map((seriesKey, index) => (
          <Flex key={seriesKey} align="center" gap={1} minW={0}>
            <Box
              w="10px"
              h="10px"
              flexShrink={0}
              bg={BAR_COLORS[index % BAR_COLORS.length]}
            />
            <Text fontSize="xs" color="#334155" noOfLines={1}>
              {seriesKey}
            </Text>
          </Flex>
        ))}
      </Flex>
      {axes?.x ? (
        <Text mt={2} textAlign="center" textStyle="textContext">
          {axes.x}
        </Text>
      ) : null}
    </Box>
  );
};
